import React, { useState, useEffect } from 'react';
import { Button, Badge, Spinner, Form } from 'react-bootstrap';
import { httpsCallable } from 'firebase/functions';
import { recordActivity } from '../../api/activityApi';
import { functions } from '../../firebase';
import { Userprofile } from '../../Helpers/UsersContext';
import { Pencil, Trash2, Shield, ShieldCheck, Globe, Smartphone, Calendar, UserPlus, Mail, Check, X } from 'lucide-react';

interface UserDetailPaneProps {
    user: Userprofile;
    onEdit: (user: Userprofile) => void;
    onDelete: (user: Userprofile) => void;
    currentUserId?: string;
    isDeletedUser?: boolean;
    getUserName: (uid?: string) => string;
    currentUserIsSuperadmin?: boolean;
}

const UserDetailPane: React.FC<UserDetailPaneProps> = ({
    user,
    onEdit,
    onDelete,
    currentUserId,
    isDeletedUser = false,
    getUserName,
    currentUserIsSuperadmin = false
}) => {
    const [statusMessage, setStatusMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

    // Email states
    const [authEmail, setAuthEmail] = useState<string | null>(null);
    const [loadingAuthEmail, setLoadingAuthEmail] = useState(false);
    const [editingEmail, setEditingEmail] = useState(false);
    const [notificationEmail, setNotificationEmail] = useState(user.email || '');
    const [savingEmail, setSavingEmail] = useState(false);

    // Firebase Functions
    const getUserAuthEmailFn = httpsCallable<{ uid: string }, { success: boolean; authEmail?: string }>(functions, 'getUserAuthEmail');
    const updateUserEmailFn = httpsCallable<{ uid: string; email: string }, { success: boolean; message?: string }>(functions, 'updateUserEmail');

    // Fetch auth email when user changes
    useEffect(() => {
        if (!isDeletedUser && user.key) {
            setLoadingAuthEmail(true);
            setAuthEmail(null);
            getUserAuthEmailFn({ uid: user.key })
                .then((result) => {
                    setAuthEmail(result.data.authEmail || null);
                })
                .catch((err) => {
                    console.error('Failed to fetch auth email:', err);
                })
                .finally(() => {
                    setLoadingAuthEmail(false);
                });
        }
    }, [user.key, isDeletedUser]);

    // Update notification email when user prop changes
    useEffect(() => {
        setNotificationEmail(user.email || '');
        setEditingEmail(false);
    }, [user.email, user.key]);

    const handleSaveEmail = async () => {
        setSavingEmail(true);
        setStatusMessage(null);

        try {
            await updateUserEmailFn({ uid: user.key, email: notificationEmail });
            recordActivity('user.updated', { targetKey: user.key });
            setEditingEmail(false);
            setStatusMessage({ type: 'success', text: 'E-postadress sparad' });
        } catch (err: any) {
            setStatusMessage({ type: 'error', text: err.message || 'Kunde inte spara e-postadress' });
        } finally {
            setSavingEmail(false);
        }
    };

    const handleCancelEditEmail = () => {
        setNotificationEmail(user.email || '');
        setEditingEmail(false);
    };

    const formatDate = (dateString?: string) => {
        if (!dateString) return 'Aldrig';
        try {
            return new Date(dateString).toLocaleDateString('sv-SE', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
        } catch {
            return 'Ogiltigt datum';
        }
    };

    // Get initials for large avatar
    const getInitials = (name: string) => {
        const parts = name.split(' ');
        if (parts.length >= 2) {
            return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
        }
        return name.substring(0, 2).toUpperCase();
    };

    return (
        <div className="user-detail-pane">
            {/* Status message */}
            {statusMessage && (
                <div className={`alert alert-${statusMessage.type === 'success' ? 'success' : 'danger'} alert-dismissible py-2 mb-3`}>
                    {statusMessage.text}
                    <button type="button" className="btn-close" onClick={() => setStatusMessage(null)} />
                </div>
            )}

            {/* Profile header */}
            <div className="user-profile-header">
                <div className={`user-profile-avatar ${user.admin ? 'admin' : ''} ${isDeletedUser ? 'deleted' : ''}`}>
                    {getInitials(user.name)}
                </div>
                <div className="user-profile-info">
                    <h3 className="user-profile-name">{user.name}</h3>
                    <div className="user-profile-uid">{user.key}</div>
                </div>
                <div className="user-profile-actions">
                    <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => onEdit(user)}
                    >
                        <Pencil size={14} className="me-1" />
                        Redigera
                    </Button>
                    {!isDeletedUser && user.key !== currentUserId && (
                        <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => onDelete(user)}
                        >
                            <Trash2 size={14} className="me-1" />
                            Radera
                        </Button>
                    )}
                </div>
            </div>

            {!isDeletedUser ? (
                <>
                    {/* Permissions section */}
                    <div className="user-detail-card">
                        <h6 className="user-detail-card-title">Behörigheter</h6>
                        <div className="user-permissions-grid">
                            <div className="user-permission-item">
                                <div className="user-permission-icon">
                                    <Shield size={20} />
                                </div>
                                <div className="user-permission-info">
                                    <div className="user-permission-label">Administratör</div>
                                    <div className="user-permission-desc">Kan hantera användare och inställningar</div>
                                </div>
                                <Badge
                                    bg={user.admin ? '' : 'secondary'}
                                    style={user.admin ? { backgroundColor: '#28aede' } : undefined}
                                    className="user-permission-badge"
                                >
                                    {user.admin ? 'Aktiverad' : 'Inaktiverad'}
                                </Badge>
                            </div>
                            {currentUserIsSuperadmin && (
                                <div className="user-permission-item">
                                    <div className="user-permission-icon">
                                        <ShieldCheck size={20} />
                                    </div>
                                    <div className="user-permission-info">
                                        <div className="user-permission-label">Superadmin</div>
                                        <div className="user-permission-desc">Kan hantera superadmins och se superadmin-funktioner</div>
                                    </div>
                                    <Badge
                                        bg={user.superadmin ? '' : 'secondary'}
                                        style={user.superadmin ? { backgroundColor: '#28aede' } : undefined}
                                        className="user-permission-badge"
                                    >
                                        {user.superadmin ? 'Aktiverad' : 'Inaktiverad'}
                                    </Badge>
                                </div>
                            )}
                            <div className="user-permission-item">
                                <div className="user-permission-icon">
                                    <Globe size={20} />
                                </div>
                                <div className="user-permission-info">
                                    <div className="user-permission-label">Webbåtkomst</div>
                                    <div className="user-permission-desc">Kan logga in på webbappen</div>
                                </div>
                                <Badge
                                    bg={user.accessToWeb ? '' : 'secondary'}
                                    style={user.accessToWeb ? { backgroundColor: '#28aede' } : undefined}
                                    className="user-permission-badge"
                                >
                                    {user.accessToWeb ? 'Aktiverad' : 'Inaktiverad'}
                                </Badge>
                            </div>
                        </div>
                    </div>

                    {/* Activity section */}
                    <div className="user-detail-card">
                        <h6 className="user-detail-card-title">Aktivitet</h6>
                        <div className="user-activity-grid">
                            <div className="user-activity-item">
                                <div className="user-activity-icon">
                                    <UserPlus size={18} />
                                </div>
                                <div className="user-activity-info">
                                    <div className="user-activity-label">Skapad</div>
                                    <div className="user-activity-value">
                                        {user.createdBy ? (
                                            <>
                                                {formatDate(user.createdAt)} av {getUserName(user.createdBy)}
                                            </>
                                        ) : (
                                            <span className="text-muted">Okänt</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="user-activity-item">
                                <div className="user-activity-icon">
                                    <Calendar size={18} />
                                </div>
                                <div className="user-activity-info">
                                    <div className="user-activity-label">Senast aktiv</div>
                                    <div className="user-activity-value">{formatDate(user.lastActive || user.lastAppStart)}</div>
                                </div>
                            </div>
                            {user.lastDeviceUsed && (
                                <div className="user-activity-item">
                                    <div className="user-activity-icon">
                                        <Smartphone size={18} />
                                    </div>
                                    <div className="user-activity-info">
                                        <div className="user-activity-label">Senaste enhet</div>
                                        <div className="user-activity-value">
                                            {user.lastDeviceUsed.model}
                                            <span className="text-muted ms-1">
                                                ({user.lastDeviceUsed.systemName} {user.lastDeviceUsed.systemVersion})
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Email section */}
                    <div className="user-detail-card">
                        <h6 className="user-detail-card-title">E-post</h6>
                        <div className="user-activity-grid">
                            {/* Login email (from Auth) */}
                            <div className="user-activity-item">
                                <div className="user-activity-icon">
                                    <Mail size={18} />
                                </div>
                                <div className="user-activity-info">
                                    <div className="user-activity-label">Inloggning</div>
                                    <div className="user-activity-value">
                                        {loadingAuthEmail ? (
                                            <Spinner size="sm" />
                                        ) : authEmail ? (
                                            authEmail
                                        ) : (
                                            <span className="text-muted">Kunde inte hämta</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                            {/* Notification email (editable) */}
                            <div className="user-activity-item">
                                <div className="user-activity-icon">
                                    <Mail size={18} />
                                </div>
                                <div className="user-activity-info" style={{ flex: 1 }}>
                                    <div className="user-activity-label">Aviseringar</div>
                                    {editingEmail ? (
                                        <div className="d-flex align-items-center gap-2 mt-1">
                                            <Form.Control
                                                type="email"
                                                size="sm"
                                                value={notificationEmail}
                                                onChange={(e) => setNotificationEmail(e.target.value)}
                                                placeholder="E-postadress för aviseringar"
                                                disabled={savingEmail}
                                                style={{ maxWidth: '250px' }}
                                            />
                                            <Button
                                                variant="success"
                                                size="sm"
                                                onClick={handleSaveEmail}
                                                disabled={savingEmail}
                                            >
                                                {savingEmail ? <Spinner size="sm" /> : <Check size={14} />}
                                            </Button>
                                            <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                onClick={handleCancelEditEmail}
                                                disabled={savingEmail}
                                            >
                                                <X size={14} />
                                            </Button>
                                        </div>
                                    ) : (
                                        <div className="d-flex align-items-center gap-2">
                                            <span className="user-activity-value">
                                                {notificationEmail || <span className="text-muted fst-italic">Ej angiven</span>}
                                            </span>
                                            <Button
                                                variant="link"
                                                size="sm"
                                                className="p-0 text-muted"
                                                onClick={() => setEditingEmail(true)}
                                            >
                                                <Pencil size={14} />
                                            </Button>
                                        </div>
                                    )}
                                    <div className="text-muted small mt-1">
                                        Används för påminnelser och aviseringar. Inloggningsadressen kan inte ändras.
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            ) : (
                /* Deleted user info */
                <div className="user-detail-card">
                    <h6 className="user-detail-card-title">Information</h6>
                    <div className="user-activity-grid">
                        <div className="user-activity-item">
                            <div className="user-activity-icon">
                                <Trash2 size={18} />
                            </div>
                            <div className="user-activity-info">
                                <div className="user-activity-label">Raderad</div>
                                <div className="user-activity-value">
                                    {formatDate(user.deletedAt)} av {getUserName(user.deletedBy)}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="alert alert-info mt-3 mb-0">
                        <small>Detta namn visas i applikationen där användaren tidigare refererades.</small>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserDetailPane;
